import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { getTrans } from 'modules/i18n';
import { MAX_RANGES_PER_DAY } from '../constants';
import { TimeRange } from '../types';

interface Props {
  label: string;
  ranges: TimeRange[];
  onChange: (ranges: TimeRange[]) => void;
  addLabel: string;
  dataTestId?: string;
}

/** Editable list of HH:mm time ranges (two native time inputs per row). */
export const RangeListEditor = ({
  label,
  ranges,
  onChange,
  addLabel,
  dataTestId = 'range-list'
}: Props) => {
  const updateRange = (index: number, patch: Partial<TimeRange>) =>
    onChange(
      ranges.map((range, i) => (i === index ? { ...range, ...patch } : range))
    );

  return (
    <Box data-testid={dataTestId} sx={{ mb: 1 }}>
      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
        {label}
      </Typography>
      {ranges.map((range, index) => (
        <Box
          key={index}
          sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
        >
          <TextField
            type="time"
            size="small"
            value={range.start}
            onChange={event =>
              updateRange(index, { start: event.target.value })
            }
            inputProps={{ 'data-testid': `${dataTestId}-start-${index}` }}
          />
          <Typography variant="body2">–</Typography>
          <TextField
            type="time"
            size="small"
            value={range.end}
            onChange={event => updateRange(index, { end: event.target.value })}
            inputProps={{ 'data-testid': `${dataTestId}-end-${index}` }}
          />
          <IconButton
            aria-label={getTrans('availabilityPage.editor.removeRange')}
            onClick={() => onChange(ranges.filter((_, i) => i !== index))}
            size="small"
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Box>
      ))}
      {ranges.length < MAX_RANGES_PER_DAY && (
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={() =>
            onChange([...ranges, { start: '09:00', end: '17:00' }])
          }
          data-testid={`${dataTestId}-add`}
        >
          {addLabel}
        </Button>
      )}
    </Box>
  );
};
